import { useMemo } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { Card, CardContent } from 'components/ui/card';
import { useGetToolAgentCount, useGetToolById } from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import type { ITool } from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { useConfigStore } from 'state/store/config/useConfigStore';
import { ComposioConfiguration } from './components/composio-configuration';
import { ComposioConnections } from './components/composio-connections';
import { EditComposioToolModal } from './components/edit-composio-tool-modal';
import { EditMcpServerModal } from './components/edit-mcp-server-modal';
import { EditToolModal } from './components/edit-tool-modal';
import { McpServerConfiguration } from './components/mcp-server-configuration';
import { ToolAPI } from './components/tool-api';
import { ToolAuthentication } from './components/tool-authentication';
import { ToolDashboard } from './components/tool-dashboard';
import { ToolDetailsLayout } from './components/tool-details-layout';
import { extractToolAgentCount, normalizeToolDetails } from './components/tool-detail-utils';
import { ToolTutorialGuide } from './components/tool-tutorial-guide';
import ConfigurationLayout from 'components/configuration/ConfigurationLayout';
import { useState } from 'react';

type ToolDetailsTab = 'authentication' | 'api' | 'tutorial-guide' | 'dashboard' | 'connections';

const DEFAULT_TAB: ToolDetailsTab = 'authentication';

export default function ToolDetailsPage() {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { toolId = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const projectKey = configurationSettings?.blocksKey || '';
  const fallbackTool = (location.state as { tool?: ITool } | null)?.tool || null;
  const rawTab = searchParams.get('tab') as ToolDetailsTab | null;
  const tab =
    rawTab === 'api' || rawTab === 'tutorial-guide' || rawTab === 'dashboard' || rawTab === 'connections'
      ? rawTab
      : DEFAULT_TAB;

  const toolQuery = useGetToolById({ toolId, projectKey }, { enabled: !!toolId && !!projectKey });
  const agentCountQuery = useGetToolAgentCount(toolId, projectKey, !!toolId && !!projectKey);

  const tool = useMemo(
    () => normalizeToolDetails(toolQuery.data, fallbackTool),
    [fallbackTool, toolQuery.data]
  );

  const associatedAgentsCount = useMemo(
    () => extractToolAgentCount(agentCountQuery.data),
    [agentCountQuery.data]
  );

  const handleTabChange = (value: ToolDetailsTab) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', value);
    navigate(`/tool-details/${encodeURIComponent(toolId)}?${params.toString()}`, {
      replace: true,
      state: location.state,
    });
  };

  if (toolQuery.isLoading && !tool) {
    return (
      <Card>
        <CardContent className="flex items-center gap-3 p-6 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading tool details...
        </CardContent>
      </Card>
    );
  }

  if (!tool) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-lg font-semibold">Tool not found</div>
          <p className="mt-2 text-sm text-muted-foreground">
            We couldn&apos;t load the requested tool details.
          </p>
        </CardContent>
      </Card>
    );
  }

  const currentTab = tool.is_open_tool && tab === 'dashboard' ? DEFAULT_TAB : tab;
  const isMcpServer = tool.type === 'mcp_server';
  const isComposio = tool.type === 'composio';
  const tabs = isComposio
    ? [
      { value: 'authentication' as ToolDetailsTab, labelKey: 'CONFIGURATION', label: 'Configuration' },
      { value: 'connections' as ToolDetailsTab, labelKey: 'CONNECTIONS', label: 'Connections' },
      { value: 'tutorial-guide' as ToolDetailsTab, labelKey: 'TUTORIAL_GUIDE', label: 'Tutorial Guide' },
    ]
    : isMcpServer
      ? [
        { value: 'authentication' as ToolDetailsTab, labelKey: 'CONFIGURATION', label: 'Configuration' },
        { value: 'tutorial-guide' as ToolDetailsTab, labelKey: 'TUTORIAL_GUIDE', label: 'Tutorial Guide' },
      ]
      : undefined;

  return (
    <ConfigurationLayout>
      <ToolDetailsLayout
        tool={tool}
        tab={currentTab}
        onTabChange={handleTabChange}
        associatedAgentsCount={associatedAgentsCount}
        tabs={tabs}
        onEdit={() => setIsEditModalOpen(true)}
        isLoading={toolQuery.isLoading}
      >
        {isComposio ? (
          <>
            {currentTab === 'authentication' ? <ComposioConfiguration tool={tool} /> : null}
            {currentTab === 'connections' ? <ComposioConnections tool={tool} /> : null}
            {currentTab === 'tutorial-guide' ? <ToolTutorialGuide tool={tool} /> : null}
          </>
        ) : isMcpServer ? (
          <>
            {currentTab === 'authentication' ? <McpServerConfiguration tool={tool} /> : null}
            {currentTab === 'tutorial-guide' ? <ToolTutorialGuide tool={tool} /> : null}
          </>
        ) : (
          <>
            {currentTab === 'authentication' ? <ToolAuthentication tool={tool} /> : null}
            {currentTab === 'api' ? <ToolAPI tool={tool} /> : null}
            {currentTab === 'tutorial-guide' ? <ToolTutorialGuide tool={tool} /> : null}
            {currentTab === 'dashboard' && !tool.is_open_tool ? <ToolDashboard tool={tool} /> : null}
          </>
        )}
      </ToolDetailsLayout>

      {isComposio ? (
        <EditComposioToolModal
          mode="edit"
          open={isEditModalOpen}
          onOpenChange={setIsEditModalOpen}
          tool={{
            id: tool.id,
            name: tool.name,
            description: tool.description,
            status: tool.status,
            visibility: tool.visibility,
            CreatedBy: tool.CreatedBy,
          }}
          isBlocksTool={tool.is_open_tool}
        />
      ) : isMcpServer ? (
        <EditMcpServerModal
          open={isEditModalOpen}
          onOpenChange={setIsEditModalOpen}
          tool={{
            id: tool.id,
            name: tool.name,
            description: tool.description,
            status: tool.status,
            visibility: tool.visibility,
            CreatedBy: tool.CreatedBy,
          }}
          isBlocksTool={tool.is_open_tool}
        />
      ) : (
        <EditToolModal
          open={isEditModalOpen}
          onOpenChange={setIsEditModalOpen}
          tool={{
            id: tool.id,
            name: tool.name,
            url: tool.APIConfig?.BaseURL || '',
            description: tool.description,
            status: tool.status,
            CreatedBy: tool.CreatedBy,
          }}
          isBlocksTool={tool.is_open_tool}
        />
      )}
    </ConfigurationLayout>
  );
}
